import { Eye, FileText } from 'lucide-react';
import { Application } from '../../features/applications/types';

interface ApplicationsTableProps {
  applications: Application[];
  hasActiveFilters: boolean;
  onReset: () => void;
  onViewApplication?: (id: string) => void;
}

export function ApplicationsTable({
  applications,
  hasActiveFilters,
  onReset,
  onViewApplication,
}: ApplicationsTableProps) {
  if (applications.length === 0) {
    return (
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-12 text-center flex flex-col items-center">
        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100 shadow-sm">
          <FileText className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900">Aucune candidature trouvée</h3>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">Aucune candidature ne correspond à vos filtres.</p>
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="mt-4 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 px-4 py-2 rounded-md transition-colors"
          >
            Afficher tout
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm whitespace-nowrap">
          <thead className="bg-slate-50/80 border-b border-slate-200">
            <tr>
              <th className="px-5 py-3.5 font-semibold text-slate-600">Référence</th>
              <th className="px-5 py-3.5 font-semibold text-slate-600">Candidat</th>
              <th className="px-5 py-3.5 font-semibold text-slate-600">Espace demandé</th>
              <th className="px-5 py-3.5 font-semibold text-slate-600">Date</th>
              <th className="px-5 py-3.5 font-semibold text-slate-600">Statut</th>
              <th className="px-5 py-3.5 font-semibold text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {applications.map((app) => (
              <tr key={app.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="px-5 py-3">
                  <span className="font-medium text-slate-900">{app.ref}</span>
                </td>
                <td className="px-5 py-3">
                  <span className="text-slate-700 font-medium">{app.candidate}</span>
                </td>
                <td className="px-5 py-3">
                  <span className="text-slate-600">{app.space}</span>
                </td>
                <td className="px-5 py-3">
                  <span className="text-slate-600">{app.date}</span>
                </td>
                <td className="px-5 py-3">
                  <StatusBadge status={app.status} />
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => onViewApplication?.(app.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                    title="Voir le dossier"
                  >
                    <Eye className="w-4 h-4" />
                    Voir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; class: string }> = {
    NEW: { label: 'Nouvelle', class: 'bg-blue-100 text-blue-800 border-blue-200' },
    REVIEWING: { label: "À l'étude", class: 'bg-amber-100 text-amber-800 border-amber-200' },
    APPROVED: { label: 'Approuvée', class: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    IN_PROGRESS: { label: 'En cours', class: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
    REJECTED: { label: 'Refusée', class: 'bg-rose-100 text-rose-800 border-rose-200' },
    CLOSED: { label: 'Clôturée', class: 'bg-slate-100 text-slate-800 border-slate-200' },
  };
  const config = configs[status] || configs.NEW;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${config.class}`}>
      {config.label}
    </span>
  );
}
